import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';

const ProductCard = ({ id, _id, name, price, discountPrice, images, image, sizes = ['S', 'M', 'L', 'XL'] }) => {
  const targetId = _id || id;
  const imageList = images && images.length > 0 ? images : image ? [image] : [];
  const mainImage = imageList[0] || null;
  const hoverImage = imageList.length > 1 ? imageList[1] : null;

  const { toggleWishlist, wishlist = [], addToCart } = useCart();
  const isWishlisted = wishlist.some(item => (item._id === targetId || item.id === targetId));

  // Quick size selector dropdown/drawer states
  const [showSizePicker, setShowSizePicker] = useState(false);
  const [addedSize, setAddedSize] = useState('');

  const handleWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist({ _id: targetId, id: targetId, name, price, discountPrice, images: imageList, image: mainImage });
  };

  const handleSelectSizeAndAdd = (e, size) => {
    e.preventDefault();
    e.stopPropagation();

    addToCart(
      {
        _id: targetId,
        id: targetId,
        name,
        price: discountPrice || price,
        discountPrice,
        image: mainImage,
        images: imageList
      },
      size
    );

    setAddedSize(size);
    setTimeout(() => {
      setAddedSize('');
      setShowSizePicker(false);
    }, 1200);
  };

  const handleTogglePicker = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSizePicker(!showSizePicker);
  };

  return (
    <div className="group relative block text-left">
      <Link to={`/product/${targetId}`} className="block no-underline">
        {/* Aspect Container */}
        <div className="relative aspect-[3/4] overflow-hidden bg-[#E5D3B3] mb-4 rounded-sm shadow-sm">
          
          {/* Wishlist Heart Action Button */}
          <button
            type="button"
            onClick={handleWishlistClick}
            className="absolute top-3 right-3 z-20 p-2 bg-[#F5F5DC]/80 backdrop-blur-sm rounded-full shadow-md transition-all duration-300 transform hover:scale-110 active:scale-95 group/heart cursor-pointer border-none"
          >
            <Heart 
              className={`w-4 h-4 transition-colors duration-300 ${
                isWishlisted 
                  ? 'fill-red-800 text-red-800' 
                  : 'text-[#2C1E12] group-hover/heart:text-red-700'
              }`} 
            />
          </button>

          {/* Main Product Image */}
          {mainImage ? (
            <img 
              src={mainImage}
              alt={`${name} Primary View`}
              className={`w-full h-full object-cover transition-all duration-700 absolute inset-0 ${
                hoverImage ? 'group-hover:opacity-0 group-hover:scale-105' : 'group-hover:scale-110'
              }`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#664229]/20 font-bold italic text-xs tracking-widest absolute inset-0">
              MODISCHE PIECE
            </div>
          )}

          {/* Swipe Hover Image */}
          {hoverImage && (
            <img 
              src={hoverImage}
              alt={`${name} Secondary View`}
              className="w-full h-full object-cover transition-all duration-700 transform scale-100 group-hover:scale-105 opacity-0 group-hover:opacity-100 absolute inset-0"
            />
          )}

          {/* SIZE SELECTION PANEL */}
          <div 
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            className={`absolute bottom-0 left-0 w-full p-3 transition-transform duration-300 bg-[#2C1E12]/90 backdrop-blur-md z-10 ${
              showSizePicker ? 'translate-y-0' : 'translate-y-full group-hover:translate-y-0'
            }`}
          >
            {addedSize ? (
              <div className="flex items-center justify-center gap-1.5 py-1.5 text-[#F5F5DC] text-[10px] font-mono font-bold uppercase tracking-wider">
                <Check className="w-3.5 h-3.5 text-green-400" />
                <span>Added ({addedSize}) to Bag</span>
              </div>
            ) : (
              <div>
                <p className="text-[9px] uppercase tracking-widest text-[#F5F5DC]/80 font-bold text-center mb-2 font-mono">
                  Select Size to Add
                </p>
                <div className="flex justify-center items-center gap-1.5 flex-wrap">
                  {sizes.map((size) => (
                    <button
                      key={size}
                      type="button"
                      onClick={(e) => handleSelectSizeAndAdd(e, size)}
                      className="px-2.5 py-1 text-[10px] font-mono font-bold bg-[#F5F5DC] text-[#2C1E12] rounded-xs hover:bg-[#E5D3B3] active:scale-95 transition shadow-sm border-none cursor-pointer"
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Item Details */}
        <h3 className="text-xs uppercase tracking-widest text-[#2C1E12] mb-1 truncate">{name}</h3>
        
        <div className="text-xs font-medium tracking-tighter">
          {discountPrice ? (
            <p className="m-0">
              <span className="line-through text-red-700/60 mr-2">₹{price}</span>
              <span className="text-[#2C1E12] font-bold">₹{discountPrice}</span>
            </p>
          ) : (
            <p className="text-[#664229] m-0">₹{price}</p>
          )}
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;